import { MODAL_TYPES } from '@components/modals/constants';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import { Button } from '@mui/material';
import { useModal } from '@store/hooks';

import type { ExperimentalAppointment } from '../../fixtures';

interface Props {
  appointment: ExperimentalAppointment;
  /** "hero" — full button suitable for the drawer's top region.
   *  "inline" — compact chip-button for the popover verb row. */
  variant?: 'hero' | 'inline';
  /** Caller can override the source tag emitted into payment events. */
  source?: string;
}

/**
 * "Collect"-state CTA. Opens the production PaymentRequestModal with the
 * patient + amount pre-filled from the appointment's readiness inputs.
 *
 * In the prototype the patient_id is a fixture ID (no real Stripe customer
 * record), so the modal opens with empty payment methods — the demo intent
 * is the interaction, not a successful charge. Production wires the same
 * modal to real patient + encounter context with no other code changes.
 */
export const SendInvoiceButton = ({
  appointment,
  variant = 'hero',
  source = 'eligibility_collect',
}: Props) => {
  const { open } = useModal();

  const due = appointment.block_state?.readiness_inputs?.eligibility?.member_obligation_cents;
  const amount = due != null ? (due / 100).toFixed(2) : undefined;

  // `patient` and `encounter` are loosely typed on the OpenAPI response;
  // narrow at the read boundary so we can pull patient_id and encounter_id
  // out without unchecked indexing.
  const patient = (
    appointment as { patient?: { patient_id?: string; first_name?: string; last_name?: string } | null }
  ).patient;
  const encounterId = (appointment.encounter as { encounter_id?: string } | null | undefined)
    ?.encounter_id;
  const patientName = formatPatientName(appointment);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    open(MODAL_TYPES.PAYMENT_REQUEST, {
      patientId: patient?.patient_id ?? undefined,
      patientName,
      encounterId,
      appointmentId: appointment.appointment_id,
      amount,
      reason: appointment.encounter_type ?? undefined,
      source,
    });
  };

  if (variant === 'inline') {
    return (
      <Button
        size="small"
        variant="contained"
        startIcon={<ReceiptOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={handleClick}
        sx={{
          ml: 'auto',
          textTransform: 'none',
          fontSize: 12,
          py: 0.25,
          minHeight: 0,
          bgcolor: ELIG_TOKENS.midnight,
          color: '#fff',
          '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
        }}
      >
        Send invoice
      </Button>
    );
  }

  return (
    <Button
      variant="contained"
      startIcon={<ReceiptOutlinedIcon />}
      onClick={handleClick}
      sx={{
        textTransform: 'none',
        bgcolor: ELIG_TOKENS.midnight,
        color: '#fff',
        '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
      }}
    >
      {amount ? `Send invoice for $${amount}` : 'Send invoice'}
    </Button>
  );
};

const formatPatientName = (appt: ExperimentalAppointment): string | undefined => {
  // The OpenAPI-generated AppointmentResponse leaves `patient` loosely typed,
  // and fixtures populate it as { first_name, last_name }. Read defensively.
  const patient = (appt as { patient?: { first_name?: string; last_name?: string } | null })
    .patient;
  if (!patient) return appt.display_title ?? undefined;
  const name = [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim();
  return name || (appt.display_title ?? undefined);
};
